import type { CatalogueResponse, Customer, CustomerAddress, CustomerOrder, CustomerOrderDetail, CustomerSession, FamilyResponse, PaymentMethod } from "../types";

const apiBase = import.meta.env.VITE_TIT_STOREFRONT_API || "https://emadmin.emirakai.com/storefront/index.php";

async function request<T>(route: string, params: Record<string, string> = {}, init: RequestInit = {}): Promise<T> {
  const separator = apiBase.includes("?") ? "&" : "?";
  const query = new URLSearchParams({ route, ...params });
  const response = await fetch(`${apiBase}${separator}${query.toString()}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...init.headers },
    ...init,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof body.error === "string" ? body.error : "The storefront service is unavailable.");
  return body as T;
}

/** The public catalogue is deliberately family-granular. */
const CATALOGUE_TIMEOUT_MS = 12000;

/** One catalogue read with a time limit, so a stalled mobile connection fails instead of hanging. */
function readCatalogue(): Promise<CatalogueResponse> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), CATALOGUE_TIMEOUT_MS);
  return request<CatalogueResponse>("families-v1", {}, { signal: controller.signal }).then((data) => {
    if (!Array.isArray(data.families)) throw new Error("The catalogue response was incomplete.");
    return data;
  }).finally(() => window.clearTimeout(timer));
}

/** The public catalogue, retried once after a short pause before giving up. */
export async function getCatalogue(): Promise<CatalogueResponse> {
  try {
    return await readCatalogue();
  } catch {
    await new Promise((resolve) => window.setTimeout(resolve, 1500));
    return readCatalogue();
  }
}

/** Family detail is fetched separately so selected SKU data is always fresh. */
export function getFamily(prodUnique: string): Promise<FamilyResponse> {
  return request<FamilyResponse>("family-v1", { prod_unique: prodUnique });
}

export function getCustomerSession(): Promise<CustomerSession> { return request("customer-session"); }

function csrfHeaders(csrf: string): HeadersInit {
  return { "Content-Type": "application/json", "X-CSRF-Token": csrf };
}

export function requestCustomerOtp(email: string, csrf: string): Promise<{ ok: true; cooldownSeconds: number }> {
  return request("customer-otp-request", {}, { method: "POST", headers: csrfHeaders(csrf), body: JSON.stringify({ email }) });
}

export function verifyCustomerOtp(email: string, code: string, csrf: string): Promise<{ customer: Customer; csrf: string }> {
  return request("customer-otp-verify", {}, { method: "POST", headers: csrfHeaders(csrf), body: JSON.stringify({ email, code }) });
}

export function getCustomerOrders(): Promise<{ customer: Customer; orders: CustomerOrder[]; csrf: string }> {
  return request("customer-orders");
}

export function getCustomerOrder(orderNumber: string): Promise<{ order: CustomerOrderDetail }> {
  return request("customer-order", { order_number: orderNumber });
}

export function updateCustomerProfile(input: { name: string; phone: string }, csrf: string): Promise<{ customer: Customer; csrf: string }> {
  return request("customer-profile", {}, { method: "POST", headers: csrfHeaders(csrf), body: JSON.stringify(input) });
}

export function getCustomerAddresses(): Promise<{ addresses: CustomerAddress[]; csrf: string }> { return request("customer-addresses"); }

export function saveCustomerAddress(input: Omit<CustomerAddress, "id"> & { id?: number }, csrf: string): Promise<{ address: CustomerAddress; addresses: CustomerAddress[]; csrf: string }> {
  return request("customer-address", {}, { method: "POST", headers: csrfHeaders(csrf), body: JSON.stringify(input) });
}

export function deleteCustomerAddress(id: number, csrf: string): Promise<{ addresses: CustomerAddress[]; csrf: string }> {
  return request("customer-address-delete", {}, { method: "POST", headers: csrfHeaders(csrf), body: JSON.stringify({ id }) });
}

export function setDefaultCustomerAddress(id: number, csrf: string): Promise<{ addresses: CustomerAddress[]; csrf: string }> {
  return request("customer-address-default", {}, { method: "POST", headers: csrfHeaders(csrf), body: JSON.stringify({ id }) });
}

export function logoutCustomer(csrf: string): Promise<CustomerSession> {
  return request("customer-logout", {}, { method: "POST", headers: csrfHeaders(csrf) });
}

export function beginPreview(password: string): Promise<{ ok: true }> {
  return request("preview-session", {}, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
}

export type CheckoutInput = {
  lines: Array<{ productId: string; quantity: number }>;
  customer: { name: string; email: string; phone: string; address: string; city: string; state: string; pin: string };
  paymentMethod: PaymentMethod;
  /** Same token for a resubmitted attempt, so the server returns the same order. */
  checkoutToken: string;
  createAccount?: boolean;
  savedAddressId?: number;
  saveAddress?: boolean;
};

export type CheckoutResponse = { orderNumber?: string; order?: { orderNumber: string }; payment?: { key: string; orderId: string; amount: number; currency: "INR" } };

export function createCheckout(input: CheckoutInput, csrf: string): Promise<CheckoutResponse> {
  return request("checkout-v11", {}, {
    method: "POST",
    headers: csrfHeaders(csrf),
    body: JSON.stringify(input),
  });
}

export function verifyRazorpay(input: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }, csrf: string): Promise<{ order: { orderNumber: string } }> {
  return request("razorpay-verify-v11", {}, {
    method: "POST",
    headers: csrfHeaders(csrf),
    body: JSON.stringify(input),
  });
}
